import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type SceneTone = 'dusk' | 'night' | 'dawn'

/** The two-layer line of hills at the foot of every painted scene. */
export function Hills({ tone = 'dark' }: { tone?: 'dark' | 'soft' }) {
  return (
    <svg className="hills" viewBox="0 0 800 120" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M0 92 L150 60 L300 98 L470 45 L620 90 L800 68 L800 120 L0 120Z"
        fill={tone === 'dark' ? 'rgb(2 8 18 / 0.5)' : 'rgb(3 20 32 / 0.35)'}
      />
      <path
        d="M0 108 L200 82 L380 110 L560 76 L800 104 L800 120 L0 120Z"
        fill={tone === 'dark' ? 'rgb(1 5 12 / 0.78)' : 'rgb(3 20 32 / 0.5)'}
      />
    </svg>
  )
}

interface SceneProps extends ComponentProps<'div'> {
  tone: SceneTone
  children: ReactNode
}

/**
 * A painted sky: dusk, night or dawn, with stars and hills. Dark in both themes,
 * so text inside is white and secondary text uses the glass tint, never grey.
 */
export function Scene({ tone, className, children, ...props }: SceneProps) {
  return (
    <div className={cn('scene', tone, className)} {...props}>
      <span className="stars" aria-hidden="true" />
      <Hills tone={tone === 'dawn' ? 'soft' : 'dark'} />
      {children}
    </div>
  )
}

/** The website's sound bars. Decorative; they stop under reduced motion. */
export function SoundBars({ className }: { className?: string }) {
  return (
    <span className={cn('eq h-[18px]', className)} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
      <i />
    </span>
  )
}
